<template>
    <v-container fluid class="pa-4">
        <hr-payslips-year-strip
            :year="filterYear"
            :summary="summary"
            :selected-month="filterMonth"
            @prev-year="prevYear"
            @next-year="nextYear"
            @new-import="openNewImport"
            @select-month="selectMonth">
        </hr-payslips-year-strip>

        <v-card class="mb-3">
            <v-card-text>
                <v-row align="center" dense>
                    <v-col cols="12" md="6">
                        <v-autocomplete
                            v-model="filterEmployee"
                            :items="employees"
                            item-text="text"
                            item-value="value"
                            label="Dipendente"
                            prepend-icon="mdi-account-tie"
                            outlined
                            dense
                            hide-details
                            clearable
                            @change="load"
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6" class="d-flex align-center justify-end">
                        <v-btn small text @click="clearMonth" v-if="filterMonth">
                            <v-icon left small>mdi-close</v-icon>Tutto l'anno
                        </v-btn>
                        <v-btn icon @click="load" :loading="loading"><v-icon>mdi-refresh</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-data-table
                :headers="headers"
                :items="items"
                :loading="loading"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1], 'items-per-page-text': 'Righe per pagina:' }"
                class="mt-2">
                <template v-slot:item.period="{ item }">
                    {{ monthName(item.period_month) }} {{ item.period_year }}
                </template>
                <template v-slot:item.net_amount="{ item }">{{ formatCurrency(item.net_amount) }}</template>
                <template v-slot:item.gross_amount="{ item }">{{ formatCurrency(item.gross_amount) }}</template>
                <template v-slot:item.published_at="{ item }">{{ formatDate(item.published_at) }}</template>
                <template v-slot:item.attachment_id="{ item }">
                    <v-btn v-if="item.attachment_id && $store.getters.canActOn('hrpayslips', 'show', item)" icon small color="success" title="Apri PDF cedolino" @click="openPayslipAttachment(item)">
                        <v-icon small>mdi-file-pdf-box</v-icon>
                    </v-btn>
                    <span v-else-if="!item.attachment_id" class="text-caption grey--text">Nessun file</span>
                    <v-tooltip v-else bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" small color="grey">mdi-lock-outline</v-icon>
                        </template>
                        <span>Non hai i permessi per visualizzare questo file</span>
                    </v-tooltip>
                </template>
            </v-data-table>
        </v-card>

        <v-card v-if="imports.length">
            <v-card-title class="text-subtitle-1">Import mensili</v-card-title>
            <v-data-table
                :headers="importHeaders"
                :items="imports"
                disable-pagination
                hide-default-footer
                dense>
                <template v-slot:item.period="{ item }">{{ monthName(item.period_month) }} {{ item.period_year }}</template>
                <template v-slot:item.status="{ item }">
                    <v-chip small :color="importStatusColor(item.status)" text-color="white">{{ importStatusText(item.status) }}</v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                    <v-btn v-if="item.employees_unmatched > 0" small text color="orange" @click="openResolveDialog(item)" class="mr-1">
                        <v-icon left small>mdi-account-search</v-icon>
                        Risolvi ({{ item.employees_unmatched }})
                    </v-btn>
                    <v-btn small text color="primary" @click="process(item)" :disabled="item.status === 'completed'">
                        <v-icon left small>mdi-cog</v-icon>
                        Processa
                    </v-btn>
                </template>
            </v-data-table>
        </v-card>

        <!-- Dialog nuovo import -->
        <v-dialog v-model="importDialog" max-width="600" persistent>
            <v-card>
                <v-card-title>
                    <v-icon left color="primary">mdi-file-import</v-icon>
                    Nuovo import mensile
                </v-card-title>
                <v-card-text>
                    <p class="text-body-2 mb-3">
                        Seleziona il mese di competenza e carica il <strong>PDF unico</strong> ricevuto dal consulente del lavoro. Il sistema spezzetta il file pagina per pagina, riconosce il <strong>Codice Fiscale</strong> e crea una busta paga nel dossier di ogni dipendente.
                    </p>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-select v-model="newImport.period_month" :items="months" label="Mese" prepend-icon="mdi-calendar-month" outlined dense></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.number="newImport.period_year" type="number" label="Anno" prepend-icon="mdi-calendar" outlined dense></v-text-field>
                        </v-col>
                    </v-row>

                    <v-file-input
                        v-model="uploadFile"
                        label="PDF buste paga (unico file)"
                        prepend-icon="mdi-file-pdf-box"
                        accept="application/pdf"
                        outlined
                        dense
                        show-size
                    ></v-file-input>

                    <v-progress-linear v-if="uploading" indeterminate color="primary" class="mb-2"></v-progress-linear>

                    <v-alert v-if="uploadResult" :type="uploadResult.unmatched > 0 ? 'warning' : 'success'" dense>
                        Riconosciuti <strong>{{ uploadResult.matched }}</strong> dipendenti su {{ uploadResult.total }} pagine.
                        <span v-if="uploadResult.unmatched > 0">
                            {{ uploadResult.unmatched }} pagine non riconosciute (codice fiscale assente o dipendente non trovato): riconciliazione manuale dalla tabella Import.
                        </span>
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text :disabled="uploading" @click="closeImportDialog">Chiudi</v-btn>
                    <v-btn
                        color="primary"
                        :loading="uploading"
                        :disabled="!newImport.period_month || !newImport.period_year || !uploadFile"
                        @click="submitImportWithFile">
                        <v-icon left small>mdi-upload</v-icon>
                        Carica e processa
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog risoluzione pagine non riconosciute -->
        <v-dialog v-model="resolveDialog" max-width="700">
            <v-card v-if="resolveTarget">
                <v-card-title>
                    <v-icon left color="primary">mdi-account-search</v-icon>
                    Pagine non riconosciute — {{ monthName(resolveTarget.period_month) }} {{ resolveTarget.period_year }}
                </v-card-title>
                <v-card-text>
                    <p class="text-body-2">Il Codice Fiscale non è stato riconosciuto automaticamente su queste pagine. Seleziona il dipendente per assegnare manualmente la busta paga.</p>
                    <v-simple-table dense>
                        <thead>
                            <tr><th>Pagina</th><th>Estratto testo</th><th>Dipendente</th><th></th></tr>
                        </thead>
                        <tbody>
                            <tr v-for="p in (resolveTarget.unmatched_pages || [])" :key="p.page_number">
                                <td class="text-center">#{{ p.page_number }}</td>
                                <td><div class="text-caption" style="max-width: 300px; white-space: normal;">{{ p.text_excerpt }}</div></td>
                                <td>
                                    <v-autocomplete
                                        v-model="p.__employeeId"
                                        :items="employees"
                                        item-text="text"
                                        item-value="value"
                                        label="Dipendente"
                                        dense
                                        outlined
                                        hide-details
                                    ></v-autocomplete>
                                </td>
                                <td>
                                    <v-btn small color="primary" :disabled="!p.__employeeId" @click="resolvePage(resolveTarget, p)">
                                        Assegna
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="resolveDialog = false">Chiudi</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import HrPayslipsYearStrip from '@/components/modules/hr/HrPayslipsYearStrip'

    export default {
        name: 'hr-payslips',

        components: { HrPayslipsYearStrip },

        data() {
            return {
                items: [],
                imports: [],
                summary: [],
                employees: [],
                filterEmployee: null,
                filterYear: new Date().getFullYear(),
                filterMonth: null,
                loading: false,
                saving: false,
                importDialog: false,
                newImport: { period_year: new Date().getFullYear(), period_month: new Date().getMonth() + 1 },
                uploadFile: null,
                uploading: false,
                uploadResult: null,
                resolveDialog: false,
                resolveTarget: null,
                itemsPerPage: 25,
                headers: [
                    { text: 'Dipendente', value: 'employee_full_name' },
                    { text: 'Periodo', value: 'period' },
                    { text: 'Netto', value: 'net_amount' },
                    { text: 'Lordo', value: 'gross_amount' },
                    { text: 'File', value: 'attachment_id', align: 'center' },
                    { text: 'Pubblicata', value: 'published_at' },
                ],
                importHeaders: [
                    { text: 'Periodo', value: 'period' },
                    { text: 'Stato', value: 'status' },
                    { text: 'Pagine tot', value: 'pages_total' },
                    { text: 'Matched', value: 'employees_matched' },
                    { text: 'Unmatched', value: 'employees_unmatched' },
                    { text: '', value: 'actions', sortable: false, align: 'end' },
                ],
                months: [
                    { value: 1, text: 'Gennaio' }, { value: 2, text: 'Febbraio' },
                    { value: 3, text: 'Marzo' }, { value: 4, text: 'Aprile' },
                    { value: 5, text: 'Maggio' }, { value: 6, text: 'Giugno' },
                    { value: 7, text: 'Luglio' }, { value: 8, text: 'Agosto' },
                    { value: 9, text: 'Settembre' }, { value: 10, text: 'Ottobre' },
                    { value: 11, text: 'Novembre' }, { value: 12, text: 'Dicembre' },
                ],
            }
        },

        methods: {
            load() {
                this.loading = true
                const payload = {}
                if (this.filterEmployee) payload.employee_id = this.filterEmployee
                if (this.filterYear) payload.year = this.filterYear
                if (this.filterMonth) payload.month = this.filterMonth
                this.axios.post('hrpayslips-filters', payload).then(r => {
                    this.items = r.data.data || []
                    this.loading = false
                }, () => { this.loading = false })
                this.axios.post('hrpayslipimports-filters', { year: this.filterYear }).then(r => {
                    this.imports = r.data.data || []
                })
                this.loadSummary()
            },
            loadSummary() {
                const params = { year: this.filterYear }
                if (this.filterEmployee) params.employee_id = this.filterEmployee
                this.axios.get('hrpayslips/yearly-summary', { params }).then(r => {
                    this.summary = r.data.summary || []
                })
            },
            selectMonth(m) { this.filterMonth = this.filterMonth === m ? null : m; this.load() },
            clearMonth() { this.filterMonth = null; this.load() },
            prevYear() { this.filterYear -= 1; this.load() },
            nextYear() { this.filterYear += 1; this.load() },
            loadEmployees() {
                this.axios.post('hremployees-filters').then(r => {
                    const data = r.data.data && r.data.data.data ? r.data.data.data : (Array.isArray(r.data.data) ? r.data.data : [])
                    this.employees = data
                        .map(e => ({ value: e.id, text: `${e.first_name || ''} ${e.last_name || ''}`.trim() || `#${e.id}` }))
                        .sort((a, b) => a.text.localeCompare(b.text, 'it', { sensitivity: 'base' }))
                })
            },
            openNewImport() {
                this.newImport = { period_year: new Date().getFullYear(), period_month: new Date().getMonth() + 1 }
                this.uploadFile = null
                this.uploadResult = null
                this.importDialog = true
            },
            closeImportDialog() {
                this.importDialog = false
                this.uploadFile = null
                this.uploadResult = null
            },
            submitImportWithFile() {
                if (!this.uploadFile) return
                this.uploading = true
                this.uploadResult = null

                this.axios.post('hrpayslipimports', this.newImport).then(r => {
                    const importId = r.data.id
                    const fd = new FormData()
                    fd.append('file', this.uploadFile)
                    this.axios.post(`hrpayslipimports/${importId}/upload`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    }).then(rr => {
                        const m = rr.data.model || {}
                        this.uploadResult = {
                            total: m.pages_total || 0,
                            matched: m.employees_matched || 0,
                            unmatched: m.employees_unmatched || 0,
                        }
                        this.uploading = false
                        this.load()
                    }, err => {
                        this.uploading = false
                        this.uploadResult = {
                            total: 0, matched: 0, unmatched: 0,
                            error: (err.response && err.response.data && err.response.data.message) || 'Errore durante il caricamento',
                        }
                    })
                }, () => { this.uploading = false })
            },
            process(item) { this.axios.post(`hrpayslipimports/${item.id}/process`).then(() => this.load()) },
            openPayslipAttachment(item) {
                this.axios.get(`hrpayslips/${item.id}/attachment`, { responseType: 'blob' }).then(r => {
                    const blob = new Blob([r.data], { type: r.headers['content-type'] || 'application/pdf' })
                    const url = URL.createObjectURL(blob)
                    window.open(url, '_blank')
                    setTimeout(() => URL.revokeObjectURL(url), 30000)
                })
            },
            openResolveDialog(item) {
                this.axios.get(`hrpayslipimports/${item.id}`).then(r => {
                    const model = r.data.model || r.data
                    this.resolveTarget = model
                    this.resolveDialog = true
                })
            },
            resolvePage(importItem, page) {
                this.axios.post(`hrpayslipimports/${importItem.id}/resolve-unmatched`, {
                    page_number: page.page_number,
                    employee_id: page.__employeeId,
                }).then(() => {
                    this.load()
                    this.openResolveDialog(importItem)
                })
            },
            monthName(m) { return this.months.find(x => x.value === m) ? this.months.find(x => x.value === m).text : '' },
            formatCurrency(v) { return v != null ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v) : '—' },
            formatDate(d) { return d ? new Date(d).toLocaleDateString('it-IT') : '—' },
            importStatusText(s) { return { pending: 'In attesa', partial: 'Parziale', completed: 'Completato', empty: 'Vuoto', failed: 'Fallito' }[s] || s },
            importStatusColor(s) { return { pending: 'orange', partial: 'blue', completed: 'green', empty: 'grey', failed: 'red' }[s] || 'grey' },
        },

        created() {
            this.$store.commit('endProgress')
            this.loadEmployees()
            this.load()
        },
    }
</script>
